import { FC, Fragment, ReactNode, useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import { useHistory } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

import {
    BackLink,
    BodyText,
    Button,
    Card,
    Collapsible,
    Eyebrow,
    Headline,
    Layout,
    Link,
    Stack,
} from '@servicetitan/design-system';

import {
    JsonSchemaCollection,
    JsonSchemaOrRef,
    JsonSchemaPrimitive,
    JsonSchemaUntyped,
    OpenApiMediaType,
    OpenApiOperation,
    OpenApiSecurityRequirement,
    OpenApiSecurityScheme,
    OpenApiSecuritySchemeApiKey,
    OpenApiSecuritySchemeOAuth2,
} from '../../api/documentation.api';
import {
    getReferencedSchemas,
    getSchemaDetailsHeader,
    getSchemaNameFromRef,
    isArraySchema,
} from '../utils';
import { SchemaDescription } from './schema-description';
import { SchemaDetails } from './schema-details';
import { SchemaDetailsDrawer } from './schema-details-drawer';
import * as Styles from './docs.module.scss';
import { getRequiredScopeText, SecurityApiKeyHeader, SecurityOAuth } from './security-details';

const knownStatusMessages: { readonly [statusCode: string]: string } = {
    200: 'OK',
    201: 'Created',
    202: 'Accepted',
    204: 'No Content',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    409: 'Conflict',
    410: 'Gone',
    422: 'Unprocessable Entity',
    500: 'Internal Server Error',
};

const MediaTypeTable: FC<{
    mediaTypes: { [mediaType: string]: OpenApiMediaType };
    formatSchemaType: (schema: JsonSchemaOrRef) => ReactNode;
}> = ({ mediaTypes, formatSchemaType }) => (
    <table className={Styles.simple}>
        <thead>
            <tr>
                <th className={Styles.mediaType}>Media Type</th>
                <th>Schema Type</th>
            </tr>
        </thead>
        <tbody>
            {Object.entries(mediaTypes).map(([key, mediaType]) => (
                <tr key={key}>
                    <td>{key}</td>
                    <td>
                        <div>{formatSchemaType(mediaType.schema)}</div>
                        {mediaType.example && (
                            <Fragment>
                                <div className={Styles.exampleHeader}>Example</div>
                                <SyntaxHighlighter language="json">
                                    {JSON.stringify(mediaType.example, null, 2)}
                                </SyntaxHighlighter>
                            </Fragment>
                        )}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

export const OperationDetails: FC<{
    backLinkLabel: string;
    backLinkPath: string;
    title: string;
    method: string;
    servers: { url: string; description?: string }[];
    operation: OpenApiOperation;
    schemas: JsonSchemaCollection;
    securitySchemes: { [name: string]: OpenApiSecurityScheme };
    securityRequirements?: OpenApiSecurityRequirement[];
}> = ({
    backLinkLabel,
    backLinkPath,
    title,
    method,
    servers,
    operation,
    schemas,
    securitySchemes,
    securityRequirements,
}) => {
    const history = useHistory();

    const [selectedSchemaNames, setSelectedSchemaNames] = useState([] as string[]);
    const [securityRequirementsOpened, setSecurityRequirementsOpened] = useState(false);

    useEffect(() => {
        document.getElementById('tabs')!.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const createSchemaLink = (name: string): ReactNode => {
        return (
            <Link
                primary
                onClick={() => {
                    const index = selectedSchemaNames.indexOf(name);
                    setSelectedSchemaNames(
                        index >= 0
                            ? selectedSchemaNames.slice(0, index + 1) // recursive schema
                            : [...selectedSchemaNames, name],
                    );
                }}
            >
                {name}
            </Link>
        );
    };

    const formatSchemaType = (schema: JsonSchemaOrRef): ReactNode => {
        return 'anyOf' in schema ? (
            <Fragment>
                {'any of '}
                {/* eslint-disable-next-line react/jsx-key */}
                {...schema.anyOf!.map(anyOf => [<br />, formatSchemaType(anyOf)]).flat()}
            </Fragment>
        ) : 'oneOf' in schema ? (
            formatSchemaType(mergeOneOf(schema))
        ) : '$ref' in schema ? (
            createSchemaLink(getSchemaNameFromRef(schema.$ref))
        ) : isArraySchema(schema) ? (
            <Fragment>
                {'array of '}
                {formatSchemaType(schema.items)}
            </Fragment>
        ) : schema.type === 'object' ? (
            'object'
        ) : schema.type ? (
            (schema.type instanceof Array ? schema.type[0] : schema.type) + // ignore "null"
            (schema.format ? ` (${schema.format})` : '')
        ) : (
            'any'
        );
    };

    const mergeOneOf = (schema: JsonSchemaPrimitive | JsonSchemaUntyped): JsonSchemaOrRef => {
        const { oneOf, ...rest } = schema;
        return { ...oneOf![0], ...rest };
    };

    const resolveSchemaRef = (ref: string) => schemas[getSchemaNameFromRef(ref)];

    const [apiKeySecuritySchemeName, apiKeySecurityScheme] = Object.entries(securitySchemes).find(
        (entry): entry is [string, OpenApiSecuritySchemeApiKey] => entry[1].type === 'apiKey',
    )!;
    const [oAuthSecuritySchemeName, oAuthSecurityScheme] = Object.entries(securitySchemes).find(
        (entry): entry is [string, OpenApiSecuritySchemeOAuth2] => entry[1].type === 'oauth2',
    )!;
    const requiredScopes = securityRequirements
        ?.map(req => req[oAuthSecuritySchemeName])
        .find(scopes => scopes !== undefined);

    return (
        <Layout>
            <Layout.Section>
                <BackLink
                    pageLabel={backLinkLabel}
                    onClick={() => {
                        history.push(backLinkPath);
                    }}
                />
            </Layout.Section>
            <Layout.Section>
                <Headline size="large">{title}</Headline>
                {operation.summary && <BodyText>{operation.summary}</BodyText>}
                {operation.description && <Markdown>{operation.description}</Markdown>}
                {requiredScopes && requiredScopes.length > 0 && (
                    <BodyText className="m-t-1">
                        <b>Required scope: </b>
                        {getRequiredScopeText(requiredScopes, oAuthSecurityScheme)}
                    </BodyText>
                )}
            </Layout.Section>
            <Layout.Section className="m-t-4">
                <Card elevation="raised">
                    <Card.Section blue>
                        <Headline className="m-y-0 c-inherit" size="medium">
                            Request
                        </Headline>
                    </Card.Section>
                    <Card.Section>
                        <Stack direction="column" spacing={3}>
                            {servers.map(server => (
                                <Stack.Item key={server.url}>
                                    {server.description && (
                                        <Eyebrow className="m-b-1">{server.description}</Eyebrow>
                                    )}
                                    <div className={Styles.operationEndpoint}>
                                        <span
                                            className={Styles.operationMethod}
                                            data-method={method}
                                        >
                                            {method}
                                        </span>
                                        <span className={Styles.operationPath}>{server.url}</span>
                                    </div>
                                </Stack.Item>
                            ))}
                        </Stack>
                    </Card.Section>
                    {securityRequirements && securityRequirements.length > 0 && (
                        <Card.Section>
                            <Stack direction="row" alignItems="center">
                                <Headline className="m-0" size="small">
                                    Authentication and Authorization
                                </Headline>
                                <Button
                                    iconName={
                                        securityRequirementsOpened ? 'expand_less' : 'expand_more'
                                    }
                                    small
                                    fill="subtle"
                                    aria-expanded={securityRequirementsOpened}
                                    aria-label="View authentication and authorization information"
                                    onClick={() =>
                                        setSecurityRequirementsOpened(!securityRequirementsOpened)
                                    }
                                />
                            </Stack>
                            <Collapsible animate open={securityRequirementsOpened}>
                                {securityRequirements.map(requirement => (
                                    <Fragment key={Object.keys(requirement).join(',')}>
                                        {requirement[apiKeySecuritySchemeName] && (
                                            <SecurityApiKeyHeader scheme={apiKeySecurityScheme} />
                                        )}
                                        {requirement[oAuthSecuritySchemeName] && (
                                            <SecurityOAuth
                                                scheme={oAuthSecurityScheme}
                                                requiredScopes={
                                                    requirement[oAuthSecuritySchemeName]
                                                }
                                            />
                                        )}
                                    </Fragment>
                                ))}
                            </Collapsible>
                        </Card.Section>
                    )}
                    <Card.Section>
                        <Headline size="small">Request Parameters</Headline>
                        <table className={Styles.simple}>
                            <thead>
                                <tr>
                                    <th className={Styles.propertyName}>Parameter Name</th>
                                    <th>In</th>
                                    <th className={Styles.required}>Required</th>
                                    <th className={Styles.schemaType}>Type</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {operation.parameters.map(parameter => (
                                    <tr key={`${parameter.in}/${parameter.name}`}>
                                        <td>
                                            <code>{parameter.name}</code>
                                        </td>
                                        <td>{parameter.in}</td>
                                        <td>{parameter.required ? 'Yes' : 'No'}</td>
                                        <td>{formatSchemaType(parameter.schema)}</td>
                                        <td className={Styles.description}>
                                            <Markdown>{parameter.description}</Markdown>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card.Section>
                    {operation.requestBody && (
                        <Card.Section>
                            <Headline size="small">Request Body</Headline>
                            <MediaTypeTable
                                mediaTypes={operation.requestBody.content}
                                formatSchemaType={formatSchemaType}
                            />
                        </Card.Section>
                    )}
                </Card>
            </Layout.Section>
            {Object.entries(operation.responses).map(([statusCode, response]) => (
                <Layout.Section key={statusCode} className="m-t-4">
                    <Card elevation="raised">
                        <Card.Section color={statusCode >= '400' ? 'red' : 'green'}>
                            <Headline className="m-y-0 c-inherit" size="medium">
                                Response: {statusCode} {knownStatusMessages[statusCode]}
                            </Headline>
                        </Card.Section>
                        {response.description && (
                            <Card.Section className={Styles.description}>
                                <Markdown>{response.description}</Markdown>
                            </Card.Section>
                        )}
                        {response.content && (
                            <Card.Section>
                                <Headline size="small">Response Body</Headline>
                                <MediaTypeTable
                                    mediaTypes={response.content}
                                    formatSchemaType={formatSchemaType}
                                />
                            </Card.Section>
                        )}
                    </Card>
                </Layout.Section>
            ))}
            <Layout.Section className="m-t-4">
                <Card elevation="raised">
                    <Card.Section purple>
                        <Headline className="m-y-0 c-inherit" size="medium">
                            Schemas
                        </Headline>
                    </Card.Section>
                    {getReferencedSchemas(operation, schemas).map(({ name, schema }) => (
                        <Card.Section key={name}>
                            <Headline id={name} size="small">
                                {getSchemaDetailsHeader(name, schema)}
                            </Headline>
                            <SchemaDescription
                                schema={schema}
                                resolveSchemaRef={resolveSchemaRef}
                            />
                            <SchemaDetails
                                name={name}
                                schema={schema}
                                formatSchemaType={formatSchemaType}
                                resolveSchemaRef={resolveSchemaRef}
                            />
                        </Card.Section>
                    ))}
                </Card>
            </Layout.Section>
            {selectedSchemaNames.length > 0 && (
                <SchemaDetailsDrawer
                    schemas={schemas}
                    selectedSchemaNames={selectedSchemaNames}
                    setSelectedSchemaNames={setSelectedSchemaNames}
                    formatSchemaType={formatSchemaType}
                    resolveSchemaRef={resolveSchemaRef}
                    onClose={() => {
                        setSelectedSchemaNames([]);
                    }}
                />
            )}
        </Layout>
    );
};
OperationDetails.displayName = 'OperationDetails';
